import React from 'react';
import {
    DefaultButton, Dialog, DialogFooter, DialogType, IButtonStyles, ICommandBarItemProps, IconButton, MessageBar,
    MessageBarType, PrimaryButton, Stack, TooltipHost,
} from '@fluentui/react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import GridComposer from '../../../../components/GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../../../models/GridConfiguration';
import { ACCESS_COMPANY, ACCESS_DENIED, IStorefrontUser } from '../../../../models/Storefront';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import StorefrontAddUsersPanel from '../../../../components/Storefront/Users/StorefrontAddUsersPanel';
import ContactDetailsModal from '../../../../components/Contacts/ContactDetailsModal';
import { useGetContactByIdQuery } from '../../../../store/Api';
import {
    useGetStorefrontUsersQuery, useRemoveStorefrontUsersMutation, useSetStorefrontUserApprovalMutation,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';

/**
 * Who can sign in to this storefront.
 *
 * The list is a server-computed view rather than a stored membership: access can come from the
 * contact's own record, their company, the organization default, or an entry added here. That is why
 * the Access column exists, and why paging, sorting and searching all happen on the server — the
 * Access value does not exist until inheritance has been resolved.
 */
/**
 * Pill styling for the row's decision buttons. `minWidth: 0` is the load-bearing part: Fluent buttons
 * carry a 80px minimum that, with two of them side by side in a grid cell, is what squeezed the
 * labels. Sizing to the text instead lets both sit comfortably in the column.
 */
const PILL: IButtonStyles = {
    root: { borderRadius: 16, height: 28, minWidth: 0, padding: '0 14px' },
    label: { fontWeight: 600, whiteSpace: 'nowrap' },
};

type RowAction = 'approve' | 'deny' | 'remove';

/**
 * What each row decision is called and what it does to the person, in one place so the dialog reads
 * consistently. Worded to distinguish the reversible decisions from the one that sends an email:
 * approving cannot be taken back, denying and removing can both be undone from this screen.
 */
const ACTION_COPY: Record<RowAction, { title: string; confirm: string; consequence: (who: string) => string }> = {
    approve: {
        title: 'Approve this signup?',
        confirm: 'Approve',
        consequence: (who) => `${who} will be able to sign in to this storefront, and will be emailed a link to do so.`,
    },
    deny: {
        title: 'Deny this signup?',
        confirm: 'Deny',
        consequence: (who) => `${who} will not be able to sign in. You can approve them here later if you change your mind.`,
    },
    remove: {
        title: 'Remove access?',
        confirm: 'Remove',
        consequence: (who) => `${who} will lose access to this storefront and will not be able to sign in. You can add them again from this screen.`,
    },
};

/** How a pending signup is referred to in the confirmation: their name, or the address if unnamed. */
const nameOf = (user: IStorefrontUser): string => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name ? `${name} (${user.emailAddress})` : (user.emailAddress ?? 'This person');
};

const StorefrontUsersPage: React.FC<RouteComponentProps<{ storefront: string }>> = (props) => {
    const storefront = props.match.params.storefront;

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(25);
    const [sortBy, setSortBy] = React.useState<string>('lastName.keyword');
    const [sortDirection, setSortDirection] = React.useState<string>('asc');
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [addOpen, setAddOpen] = React.useState(false);
    const [editingContactId, setEditingContactId] = React.useState<string | undefined>();
    // The row decision waiting on confirmation. All three consequences land on a real person -- an
    // email into a stranger's inbox, a customer turned away, an existing customer locked out -- and
    // the controls sit a line apart in a grid, so none of them happens straight off the click.
    const [pendingAction, setPendingAction] = React.useState<{ user: IStorefrontUser; kind: RowAction } | undefined>();

    const users = useGetStorefrontUsersQuery({ storefront, page, pageSize, sortBy, sortDirection, searchTerm });
    const [removeUsers] = useRemoveStorefrontUsersMutation();
    const [setApproval] = useSetStorefrontUserApprovalMutation();
    // Fetched only once a row is opened. The modal edits the contact's own storefront assignment —
    // which is precisely the access this screen cannot revoke — so it doubles as the way to fix a
    // Direct or Via-company row.
    const editingContact = useGetContactByIdQuery(
        { contactId: editingContactId ?? '', includeCompanyDetails: true },
        { skip: !editingContactId },
    );

    const onRemove = async (user: IStorefrontUser) => {
        setPendingAction(undefined);
        if (!user.contactId) return;
        const result = await removeUsers({ storefront, contactIds: [user.contactId] }).unwrap();
        if (result.updated > 0) toast.success(`Removed ${user.emailAddress} from this storefront.`);
        else toast.info(result.skipped?.[0]?.reason ?? 'Nothing was removed.');
    };

    const onSetApproval = async (user: IStorefrontUser, approve: boolean) => {
        setPendingAction(undefined);
        if (!user.contactId) return;
        const result = await setApproval({ storefront, contactIds: [user.contactId], approve }).unwrap();
        // Approving can partly succeed: the decision sticks even if the notification email fails, and
        // the skip reason says so. Reporting a flat success there would hide a person who is now
        // approved but has no idea.
        if (result.skipped?.length) {
            // Longer than the default: this is the partial-success case (the decision stuck but the
            // email did not), and it is the one worth reading rather than glancing at.
            toast.info(result.skipped[0].reason, { autoClose: 8000 });
        } else {
            toast.success(approve
                ? `${user.emailAddress} approved. They have been emailed a link to sign in.`
                : `${user.emailAddress} denied.`);
        }
    };

    const getCustomColumns = (): IColumnDetails[] => [
        // Approve / Deny, shown only while a request is actually pending. Placed before the details
        // pencil because clearing the queue is what brings anyone to this screen.
        {
            identifier: 'approval',
            // Labelled, and never blank: a column that is empty for most rows and holds two buttons
            // for a few reads as a rendering fault. Saying "Approved" states the row's actual
            // standing, which is the question this column answers.
            label: 'Approval',
            type: 'Text',
            required: true,
            index: 0,
            effectiveWidth: 180,
            minimumWidth: 180,
            // Named rather than a check and a cross. These two decide whether a real person gets into
            // the store, they are the reason this screen is open, and a tooltip is no help to someone
            // deciding which of two adjacent icons to press.
            //
            // No icons: with the words carrying the meaning, an icon only competed with them for the
            // width of a grid cell, which is what made the labels crowd their buttons.
            component: (user: IStorefrontUser) => {
                if (user.pending) {
                    return (
                        <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign='center' style={{ height: '100%' }}>
                            <PrimaryButton text='Approve' styles={PILL} onClick={() => setPendingAction({ user, kind: 'approve' })} />
                            <DefaultButton text='Deny' styles={PILL} onClick={() => setPendingAction({ user, kind: 'deny' })} />
                        </Stack>
                    );
                }
                // Denying is reversible, and both this screen and its confirmation say so -- so the
                // way back has to be here. Without it a mis-click was final.
                if (user.accessSource === ACCESS_DENIED) {
                    return (
                        <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign='center' style={{ height: '100%' }}>
                            <span style={{ color: '#a4262c' }}>Denied</span>
                            <PrimaryButton text='Approve' styles={PILL} onClick={() => setPendingAction({ user, kind: 'approve' })} />
                        </Stack>
                    );
                }
                return <span>Approved</span>;
            },
        },
        // Details/edit leftmost, per the house pattern for these grids. Rows are a VIEW over contacts
        // and carry only the id, so the full contact is fetched on demand when one is opened.
        {
            identifier: 'editContact',
            label: '',
            type: 'Text',
            required: true,
            index: 1,
            effectiveWidth: 50,
            minimumWidth: 50,
            component: (user: IStorefrontUser) => (
                <TooltipHost content='Contact details'>
                    <IconButton
                        iconProps={{ iconName: 'Edit' }}
                        disabled={!user.contactId}
                        onClick={() => setEditingContactId(user.contactId)}
                    />
                </TooltipHost>
            ),
        },
        // Remove. Rendered disabled with an explanation rather than hidden, so it's clear the row
        // CAN'T be revoked from here and where to go instead — hiding it would just read as an
        // inconsistent grid. Editing the contact above is that "where", which is why it sits next to it.
        {
            identifier: 'removeAccess',
            label: '',
            type: 'Text',
            required: true,
            index: 2,
            effectiveWidth: 50,
            minimumWidth: 50,
            component: (user: IStorefrontUser) => user.revocable
                ? (
                    <TooltipHost content='Remove access to this storefront'>
                        <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => setPendingAction({ user, kind: 'remove' })} />
                    </TooltipHost>
                )
                : (
                    <TooltipHost content={`Access comes from ${user.accessSource === ACCESS_COMPANY ? 'their company' : 'the organization default'}, which is shared by everyone under it — removing just this person has to be done there.`}>
                        <IconButton iconProps={{ iconName: 'Delete' }} disabled />
                    </TooltipHost>
                ),
        },
    ];

    const additionalCommands: ICommandBarItemProps[] = [
        {
            key: 'add',
            text: 'Add Users',
            iconProps: { iconName: 'AddFriend' },
            onClick: () => setAddOpen(true),
        },
    ];

    const additionalFarCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => { users.refetch(); },
        },
    ];

    return (
        <div>
            <StorefrontSettingsTitleBar title='Users' storefront={storefront} />

            {/* Only appears when the server truncated the list. A short list here would otherwise read
                as the complete membership. */}
            {users.data?.notice && (
                <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: '1em' } }}>
                    {users.data.notice}
                </MessageBar>
            )}

            <GridComposer
                data={users.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={users.isLoading || users.isFetching}
                selectionMode={GridSelectionMode.none}
                columnSetName={'StorefrontUser'}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => { setSortBy(column ?? 'lastName.keyword'); setSortDirection(direction ?? 'asc'); }}
                onFilter={(filter) => { setSearchTerm(filter ?? ''); setPage(1); }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={false}
                // The settings layout scrolls its own content area, so the table must not also size
                // itself to the viewport — that is what produces two scrollbars on this page.
                useContentHeight
                additionalCommands={additionalCommands}
                additionalFarCommands={additionalFarCommands}
                getItemId={(user: IStorefrontUser) => user.contactId ?? ''}
            />

            {/* Mounted only with a loaded contact: the modal seeds its fields from the contact prop on
                first render, so opening it empty and filling in later would leave a blank form. */}
            {editingContactId && editingContact.data && (
                <ContactDetailsModal
                    isOpen
                    contact={editingContact.data}
                    onDismiss={(saved: boolean) => {
                        setEditingContactId(undefined);
                        // A saved contact may have changed its storefront assignment, which is exactly
                        // what this grid is a view of — so the list has to be re-resolved.
                        if (saved) users.refetch();
                    }}
                />
            )}

            {/* Names the person and says what the click actually does. One dialog for all three
                decisions: the difference between them is wording, not behavior, and a second copy
                of this is how the three drift into describing themselves differently. */}
            <Dialog
                hidden={!pendingAction}
                onDismiss={() => setPendingAction(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: pendingAction ? ACTION_COPY[pendingAction.kind].title : undefined,
                    subText: pendingAction
                        ? ACTION_COPY[pendingAction.kind].consequence(nameOf(pendingAction.user))
                        : undefined,
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text={pendingAction ? ACTION_COPY[pendingAction.kind].confirm : ''}
                        onClick={() => {
                            if (!pendingAction) return;
                            if (pendingAction.kind === 'remove') onRemove(pendingAction.user);
                            else onSetApproval(pendingAction.user, pendingAction.kind === 'approve');
                        }}
                    />
                    <DefaultButton text='Cancel' onClick={() => setPendingAction(undefined)} />
                </DialogFooter>
            </Dialog>

            <StorefrontAddUsersPanel
                isOpen={addOpen}
                storefront={storefront}
                onDismiss={(added) => {
                    setAddOpen(false);
                    if (added > 0) toast.success(`${added} added to this storefront.`);
                }}
            />
        </div>
    );
};

export default StorefrontUsersPage;
